import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Layers, PawPrint, Plus, ShoppingBag, Pencil,
  AlertTriangle, TrendingDown, Droplets,
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import OpenBagModal from '../pets/components/OpenBagModal';
import AddExtraBagsModal from '../pets/components/AddExtraBagsModal';
import RefillModal from '../pets/components/RefillModal';
import WaterButton from '../pets/components/WaterButton';
import StatusTrafficLight from '../pets/components/StatusTrafficLight';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../contexts/HydrationGroupsContext';
import { usePets } from '../../contexts/PetsContext';
import { computeHydrationStatus } from '../../hooks/useHydrationStatus';
import { formatGrams, formatTimeAgo, formatList } from '../../lib/format';
import { sumDispenserStock, sumDispenserCapacity } from '../../services/feedingGroupService';
import { estimateGroupProjection } from '../../lib/learning';
import { ROUTES, INVENTORY } from '../../config/constants';

/**
 * Vista station-céntrica del inventario.
 * Lista los grupos del hogar con su stock y qué mascotas usan cada uno.
 * Stage 2: cada dispenser del grupo se muestra individualmente con su propio
 * botón "Rellenar". El semáforo del grupo es la suma agregada.
 */
const InventoryPage = () => {
  const { groups, isLoading: groupsLoading } = useFeedingGroups();
  const { groups: hydrationGroups } = useHydrationGroups();
  const { pets } = usePets();

  // Estado para los modales: group y/o (group + dispenser).
  const [openBagModalGroup, setOpenBagModalGroup] = useState(null);
  const [addBagsModalGroup, setAddBagsModalGroup] = useState(null);
  // refill: { group, dispenser }
  const [refillTarget, setRefillTarget] = useState(null);

  const petsByGroup = useMemo(() => {
    const map = new Map();
    for (const pet of pets) {
      if (!pet.feedingGroupId) continue;
      const arr = map.get(pet.feedingGroupId) ?? [];
      arr.push(pet);
      map.set(pet.feedingGroupId, arr);
    }
    return map;
  }, [pets]);

  // Fase 6: calcular proyección por grupo + ordenar para que los más urgentes
  // queden arriba. El ranking es: isOutOfStock primero, después isLowStock,
  // dentro de cada bucket por daysRemaining asc, después por nombre.
  const groupsWithProjection = useMemo(() => {
    const enriched = groups.map((group) => ({
      group,
      groupPets: petsByGroup.get(group.id) ?? [],
      projection: estimateGroupProjection(group, petsByGroup.get(group.id) ?? [], {
        defaultThresholdDays: INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS,
      }),
    }));

    return enriched.sort((a, b) => {
      const aOut = a.projection?.isOutOfStock ? 1 : 0;
      const bOut = b.projection?.isOutOfStock ? 1 : 0;
      if (aOut !== bOut) return bOut - aOut;

      const aLow = a.projection?.isLowStock ? 1 : 0;
      const bLow = b.projection?.isLowStock ? 1 : 0;
      if (aLow !== bLow) return bLow - aLow;

      // Dentro del bucket, los que menos tiempo tienen primero.
      const aDays = a.projection?.daysRemaining ?? Infinity;
      const bDays = b.projection?.daysRemaining ?? Infinity;
      if (aDays !== bDays) return aDays - bDays;

      return a.group.name.localeCompare(b.group.name);
    });
  }, [groups, petsByGroup]);

  const lowStockCount = groupsWithProjection.filter(
    (g) => g.projection?.isLowStock || g.projection?.isOutOfStock
  ).length;

  if (groupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-sm text-slate-500">Stock por grupo de alimentación</p>
        </div>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-sm text-slate-500">Stock por grupo de alimentación</p>
        </div>
        <Card className="flex flex-col items-center gap-4 py-12 text-center">
          <Package className="h-10 w-10 text-slate-300" aria-hidden="true" />
          <p className="text-sm text-slate-500 max-w-sm">
            Todavía no hay grupos. Creá uno para empezar a manejar el dispenser y la bolsa.
          </p>
          <Button as={Link} to={ROUTES.GROUP_NEW} variant="primary" leftIcon={Plus}>
            Crear grupo
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-sm text-slate-500">
            {groups.length} {groups.length === 1 ? 'grupo' : 'grupos'} de alimentación
          </p>
        </div>
        <Button as={Link} to={ROUTES.GROUP_NEW} variant="secondary" size="sm" leftIcon={Plus}>
          Crear
        </Button>
      </div>

      {/* Banner de proyecciones (Fase 6): solo aparece si hay grupos urgentes
          o si hay datos suficientes para mostrar proyecciones. Linkea a la
          pantalla detallada. */}
      {lowStockCount > 0 ? (
        <Link
          to={ROUTES.INVENTORY_PROJECTIONS}
          className="flex items-center gap-3 rounded-xl bg-red-50 ring-1 ring-red-100 px-4 py-3 hover:bg-red-100 transition-colors"
        >
          <AlertTriangle className="h-5 w-5 shrink-0 text-status-red" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-status-red">
              {lowStockCount} {lowStockCount === 1 ? 'grupo necesita' : 'grupos necesitan'} reposición pronto
            </p>
            <p className="text-xs text-red-700/80">Tocá para ver las proyecciones del hogar.</p>
          </div>
        </Link>
      ) : (
        <Link
          to={ROUTES.INVENTORY_PROJECTIONS}
          className="flex items-center gap-3 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3 hover:bg-slate-100 transition-colors"
        >
          <TrendingDown className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-700">Proyecciones del hogar</p>
            <p className="text-xs text-slate-500">Ver cuántos días de stock queda en cada grupo.</p>
          </div>
        </Link>
      )}

      <div className="flex flex-col gap-3">
        {groupsWithProjection.map(({ group, groupPets, projection }) => {
          const dispensers = group.dispensers ?? [];
          const hasDispensers = dispensers.length > 0;
          const totalDispenserStock = sumDispenserStock(dispensers);
          const totalDispenserCapacity = sumDispenserCapacity(dispensers);
          const aggregatePct =
            totalDispenserCapacity > 0
              ? Math.round((totalDispenserStock / totalDispenserCapacity) * 100)
              : 0;

          // Badge state según projection.
          const showLowStockBadge =
            projection?.isOutOfStock || projection?.isLowStock;
          const showDaysChip =
            projection?.daysRemaining != null && !showLowStockBadge;

          return (
            <Card key={group.id}>
              <div className="flex items-start gap-3 mb-3">
                <div className="rounded-lg bg-brand-100 p-2.5">
                  <Layers className="h-5 w-5 text-brand-700" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-slate-900 truncate">{group.name}</h2>
                    {showLowStockBadge && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-status-red ring-1 ring-red-100">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        {projection.isOutOfStock
                          ? 'Sin stock'
                          : `Comprar pronto · ~${Math.floor(projection.daysRemaining)}d`}
                      </span>
                    )}
                    {showDaysChip && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-100">
                        ~{Math.floor(projection.daysRemaining)}d de stock
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {groupPets.length === 0
                      ? 'Sin mascotas asignadas'
                      : `${groupPets.length} ${groupPets.length === 1 ? 'mascota' : 'mascotas'}`}
                    {!hasDispensers && ' · sin dispenser'}
                  </p>
                </div>
                <Button
                  as={Link}
                  to={`/groups/${group.id}/edit`}
                  variant="ghost"
                  size="sm"
                  aria-label={`Editar ${group.name}`}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>

              {/* Bolsa principal — siempre visible */}
              <div className="rounded-lg bg-slate-50 px-3 py-2.5 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Bolsa principal</span>
                  <span className="text-lg font-bold text-slate-900 leading-none">
                    {formatGrams(group.mainStockGr)}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {group.extraBagsCount > 0
                    ? `+ ${group.extraBagsCount} bolsa${group.extraBagsCount === 1 ? '' : 's'} en reserva`
                    : 'Sin reserva'}
                </p>
              </div>

              {/* Dispensers — lista individual */}
              {hasDispensers && (
                <div className="flex flex-col gap-2 mb-3">
                  {dispensers.length > 1 && (
                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-1">
                      <span>{dispensers.length} dispensers</span>
                      <span>
                        Total: {formatGrams(totalDispenserStock)} ({aggregatePct}%)
                      </span>
                    </div>
                  )}
                  {dispensers.map((d) => {
                    const pct =
                      d.capacityGr > 0
                        ? Math.round(((d.stockGr ?? 0) / d.capacityGr) * 100)
                        : 0;
                    return (
                      <div
                        key={d.id}
                        className="rounded-lg bg-slate-50 px-3 py-2.5 flex items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {formatGrams(d.stockGr ?? 0)}
                            <span className="text-slate-300"> · </span>
                            <span className="text-slate-400">
                              {pct}% de {formatGrams(d.capacityGr)}
                            </span>
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          leftIcon={Package}
                          onClick={() => setRefillTarget({ group, dispenser: d })}
                        >
                          Rellenar
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {groupPets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {groupPets.map((p) => (
                    <Link
                      key={p.id}
                      to={`/pets/${p.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 transition-colors"
                    >
                      <PawPrint className="h-3 w-3" aria-hidden="true" />
                      {p.name}
                    </Link>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={ShoppingBag}
                  onClick={() => setOpenBagModalGroup(group)}
                >
                  Abrir bolsa
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={Plus}
                  onClick={() => setAddBagsModalGroup(group)}
                >
                  Compré bolsas
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Sección "Baldes de agua" — independiente de los grupos de comida. */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Baldes de agua</h2>
          <p className="text-xs text-slate-500">
            Hidratación opcional por mascota. Mascotas que comparten balde se
            registran juntas.
          </p>
        </div>
        <Button
          as={Link}
          to={ROUTES.WATER_GROUP_NEW}
          variant="secondary"
          size="sm"
          leftIcon={Plus}
        >
          Nuevo balde
        </Button>
      </div>

      {hydrationGroups.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500">
            Todavía no creaste ningún balde. Activá la hidratación al editar
            una mascota y elegí o creá un balde.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {hydrationGroups.map((g) => {
            const associatedPets = pets.filter(
              (p) => p.hydrationGroupId === g.id && !p.deletedAt
            );
            const lastMs = g.lastWateredAt?.toMillis?.() ?? null;
            const status = computeHydrationStatus({
              lastWateredMs: lastMs,
              intervalHours: g.hydrationIntervalHours,
              nowMs: Date.now(),
            });
            return (
              <Card key={g.id}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="rounded-lg bg-sky-100 p-2.5">
                    <Droplets className="h-5 w-5 text-sky-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {g.name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Cada {g.hydrationIntervalHours}h
                        </p>
                      </div>
                      <Link
                        to={`/water-groups/${g.id}/edit`}
                        className="text-slate-400 hover:text-slate-700 transition-colors shrink-0"
                        aria-label={`Editar ${g.name}`}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mb-3">
                  <StatusTrafficLight status={status.status} variant="badge" />
                  {lastMs && (
                    <span className="text-xs text-slate-500">
                      {formatTimeAgo(lastMs)}
                    </span>
                  )}
                </div>

                {associatedPets.length > 0 ? (
                  <p className="text-xs text-slate-600 mb-3">
                    Toman acá:{' '}
                    <strong>{formatList(associatedPets.map((p) => p.name))}</strong>
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mb-3">
                    Sin mascotas asociadas todavía.
                  </p>
                )}

                <WaterButton group={g} variant="full" />
              </Card>
            );
          })}
        </div>
      )}

      {refillTarget && (
        <RefillModal
          group={refillTarget.group}
          dispenser={refillTarget.dispenser}
          isOpen={!!refillTarget}
          onClose={() => setRefillTarget(null)}
        />
      )}
      {openBagModalGroup && (
        <OpenBagModal
          group={openBagModalGroup}
          isOpen={!!openBagModalGroup}
          onClose={() => setOpenBagModalGroup(null)}
          onBuyBagsClick={() => setAddBagsModalGroup(openBagModalGroup)}
        />
      )}
      {addBagsModalGroup && (
        <AddExtraBagsModal
          group={addBagsModalGroup}
          isOpen={!!addBagsModalGroup}
          onClose={() => setAddBagsModalGroup(null)}
        />
      )}
    </div>
  );
};

export default InventoryPage;
